import React from 'react';
import { FileIcon, Download, AlertCircle, RotateCcw } from 'lucide-react';
import { MarkdownText } from './MarkdownText';
import { ArtifactRenderer } from './ArtifactRenderer';
import type { DisplayMessage, WidgetConfig } from '../types';

interface MessageBubbleProps {
  message: DisplayMessage;
  config: WidgetConfig;
  onRetry?: (messageId: string) => void;
  onArtifactAction?: (action: string, data: unknown) => void;
  showTimestamp?: boolean;
}

/** Returns '#ffffff' for dark backgrounds, '#1f2937' for light ones. */
function contrastTextColor(hexBg: string): string {
  const hex = hexBg.replace('#', '');
  if (hex.length < 6) return '#1f2937';
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  // Relative luminance (sRGB)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#1f2937' : '#ffffff';
}

function isSafeUrl(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.href);
    return ['https:', 'http:', 'blob:'].includes(parsed.protocol);
  } catch {
    return false;
  }
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({ message, config, onRetry, onArtifactAction, showTimestamp = true }) => {
  const { branding } = config;
  const isAgent = message.role === 'agent';
  const isCustomer = message.role === 'customer';
  const isSystem = message.role === 'system';

  if (isSystem) {
    return (
      <div className="flex justify-center">
        <p className="text-xs text-gray-400 italic px-3 py-1">{message.content}</p>
      </div>
    );
  }

  const bubbleColor = isAgent
    ? branding.agent_bubble_color
    : branding.customer_bubble_color;

  const textColor = isAgent ? '#1f2937' : contrastTextColor(branding.customer_bubble_color);

  const isImage = message.file && message.file.mime_type.startsWith('image/');

  return (
    <div className={`flex ${isCustomer ? 'justify-end' : 'justify-start'}`}>
      <div
        className="max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed"
        style={{ backgroundColor: bubbleColor, color: textColor }}
      >
        {/* Text content — markdown for agent, plain for customer */}
        {message.content && (
          isAgent
            ? <MarkdownText content={message.content} className="space-y-1.5 [&_a]:underline [&_a]:decoration-1 [&_a]:underline-offset-2 [&_strong]:font-semibold [&_em]:italic [&_h3]:mt-2 [&_h3]:mb-0.5 [&_h4]:mt-1.5 [&_h4]:mb-0.5 [&_hr]:my-2 [&_pre]:my-1.5 [&_blockquote]:my-1.5 [&_ul]:my-1 [&_ol]:my-1" />
            : <p className="whitespace-pre-wrap break-words">{message.content}</p>
        )}

        {/* File attachment */}
        {message.file && (
          <div className="mt-2">
            {isImage ? (
              <img
                src={isSafeUrl(message.file.url) ? message.file.url : ''}
                alt={message.file.filename}
                className="rounded-lg max-w-full max-h-48 object-cover cursor-pointer"
                role="button"
                tabIndex={0}
                aria-label={`Open ${message.file.filename} in new tab`}
                onClick={() => isSafeUrl(message.file!.url) && window.open(message.file!.url, '_blank')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    isSafeUrl(message.file!.url) && window.open(message.file!.url, '_blank');
                  }
                }}
              />
            ) : (
              <a
                href={isSafeUrl(message.file.url) ? message.file.url : '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-2 rounded-lg bg-black/10 hover:bg-black/15 transition-colors"
              >
                <FileIcon size={16} />
                <span className="text-xs truncate flex-1">{message.file.filename}</span>
                <Download size={14} />
              </a>
            )}
          </div>
        )}

        {/* Non-text parts (buttons, etc.) — text parts are skipped because
            message.content is already rendered above. The connector's
            buildMessageParts() always duplicates the text content into a
            { type: 'text' } part, so rendering it here would show the
            same content twice. */}
        {message.parts && message.parts.some((p) => p.type !== 'text') && (
          <div className="mt-2 space-y-1.5">
            {message.parts.map((part, i) => {
              if (part.type === 'url_button' && part.url) {
                return (
                  <button
                    key={i}
                    onClick={() => isSafeUrl(part.url!) && window.open(part.url!, '_blank')}
                    className="block w-full text-center text-xs font-medium px-3 py-2 rounded-lg border border-current/20 hover:opacity-80 transition-opacity"
                  >
                    {part.label || part.url}
                  </button>
                );
              }
              if (part.type === 'artifact' && part.artifact) {
                return (
                  <ArtifactRenderer
                    key={i}
                    artifact={part.artifact}
                    onAction={onArtifactAction || (() => {})}
                  />
                );
              }
              return null;
            })}
          </div>
        )}

        {/* Timestamp (only on last in group) + status */}
        {(showTimestamp || message.status === 'failed') && (
          <div className="flex items-center gap-1.5 mt-1">
            {showTimestamp && (
              <p className="text-[10px] opacity-50">
                {new Date(message.timestamp).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            )}
            {message.status === 'failed' && (
              <button
                onClick={() => onRetry?.(message.id)}
                className="flex items-center gap-0.5 text-[10px] text-red-400 hover:text-red-500 transition-colors"
                aria-label="Retry sending message"
              >
                <AlertCircle size={10} />
                <span>Failed</span>
                <RotateCcw size={9} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
